/**
 * YouTube Video Service
 *
 * Fetches videos from the @promptusai YouTube channel via api.promptus.ai.
 * - Backend fetches the YouTube RSS feed server-side (no CORS issues)
 * - Caches results in IndexedDB for 4 hours
 * - Falls back to hardcoded videos if API is unavailable or user not logged in
 */

import { db } from '../db';
import { getApiBaseUrl, getAuthorizationToken } from '../api-helper';

// ============================================
// Configuration
// ============================================

const CACHE_KEY = 'youtube_videos_cache_v4';
const CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours

// ============================================
// In-memory state
// ============================================

let cachedVideos = null;
let lastFetchTimestamp = 0;
let initPromise = null;

const listeners = new Set();

// ============================================
// Tag extraction for contextual matching
// ============================================

const PAGE_TAGS = {
    generate: ['generate', 'getting-started', 'playground', 'prompt', 'image-generation'],
    templates: ['templates', 'template-creator', 'presets'],
    cosyflows: ['cosyflows', 'cosyflow-editor', 'workflow'],
    'my-collection': ['collection', 'gallery', 'organize'],
    settings: ['settings', 'configuration', 'setup'],
    comfyui: ['comfyui', 'advanced', 'nodes'],
    explore: ['explore', 'community', 'public'],
    profile: ['account', 'profile', 'login'],
    momm: ['momm', 'model-manager'],
};

const KEYWORD_MAP = {
    'generate': ['generate', 'playground', 'create image', 'image generation', 'unfiltered', 'video generator', 'video generation'],
    'getting-started': ['getting started', 'beginner', 'tutorial', 'how to', 'introduction', 'guide', 'install', 'setup', 'full demo'],
    'prompt': ['prompt', 'day of 30', '30 day', 'art challenge'],
    'image-generation': ['image', 'photo', 'picture', 'art', 'design', 'flux', 'nano banana', 'z-image'],
    'templates': ['template', 'preset'],
    'cosyflows': ['cosyflow', 'workflow', 'cosy'],
    'cosyflow-editor': ['cosyflow', 'workflow editor'],
    'workflow': ['workflow', 'full workflow'],
    'comfyui': ['comfyui', 'comfy ui', 'nodes', 'stable diffusion ui'],
    'settings': ['install', 'local', 'setup', 'configure', 'localhost', 'locally'],
    'playground': ['playground', 'suno', 'flux', 'model', 'kling', 'seedance', 'dreamactor', 'ltx'],
};

function extractTags(title, description) {
    const text = `${title} ${description}`.toLowerCase();
    const tags = [];

    for (const [tag, keywords] of Object.entries(KEYWORD_MAP)) {
        if (keywords.some(kw => text.includes(kw))) {
            tags.push(tag);
        }
    }

    if (tags.length === 0) {
        tags.push('prompt', 'image-generation');
    }

    return tags;
}

// ============================================
// Fallback videos (shown before login or if API unavailable)
// ============================================

const FALLBACK_VIDEOS = [
    {
        id: '1HjJb8vhlfk',
        title: 'Did Seedance 2.0 take the top spot for AI video?',
        description: 'Comparing Seedance 2.0 against other AI video generation models.',
        publishedAt: '2026-02-11T13:26:18+00:00',
        tags: ['generate', 'playground', 'getting-started'],
    },
    {
        id: 'LCdyFPK3LTE',
        title: 'Run Z-Image Locally: Full Setup Tutorial (No Cloud Needed)',
        description: 'Full setup tutorial for running Z-Image locally on your own PC.',
        publishedAt: '2026-02-10T09:40:42+00:00',
        tags: ['cosyflows', 'cosyflow-editor', 'comfyui', 'getting-started', 'settings'],
    },
    {
        id: 'jE8OwZpOOI8',
        title: 'LTX 2 Is the BEST Open-Source Video Generator - Full Workflow',
        description: 'Full workflow tutorial for the LTX 2 open-source video generation model.',
        publishedAt: '2026-01-15T22:57:42+00:00',
        tags: ['generate', 'cosyflows', 'workflow', 'getting-started'],
    },
    {
        id: 'JF4Q5hXh-_Q',
        title: 'FINALLY: The #1 Open-Source AI Image Generator Is Here (Run It in Promptus)',
        description: 'How to run the top open-source AI image generator inside Promptus.',
        publishedAt: '2025-12-11T16:27:07+00:00',
        tags: ['generate', 'image-generation', 'getting-started', 'comfyui'],
    },
    {
        id: 'xGUTWeLTeIc',
        title: 'FLUX 2 Should Not Be FREE... It\'s Unreal (Full Demo + Workflow)',
        description: 'Full demo and workflow for the FLUX 2 model in Promptus.',
        publishedAt: '2025-11-28T14:15:35+00:00',
        tags: ['generate', 'cosyflows', 'workflow', 'image-generation'],
    },
];

// ============================================
// API Fetching
// ============================================

/**
 * Fetch YouTube videos from the Promptus API backend
 */
async function fetchFromAPI() {
    try {
        const token = getAuthorizationToken();
        const apiBase = await getApiBaseUrl();
        const response = await fetch(`${apiBase}/api/youtube/videos`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: AbortSignal.timeout(10000),
        });

        if (!response.ok) {
            console.warn('[YouTubeService] API returned', response.status);
            return null;
        }

        const data = await response.json();
        const rawVideos = data.videos || data;

        if (!Array.isArray(rawVideos) || rawVideos.length === 0) {
            console.warn('[YouTubeService] API returned empty videos');
            return null;
        }

        // Transform and add tags for contextual matching
        const videos = rawVideos.map(v => ({
            id: v.id || v.videoId,
            title: v.title || '',
            description: v.description || '',
            publishedAt: v.publishedAt || v.published || '',
            thumbnail: v.thumbnail || '',
            tags: v.tags || extractTags(v.title || '', v.description || ''),
        }));

        console.log('[YouTubeService] Fetched', videos.length, 'videos from API');
        return videos;
    } catch (e) {
        console.error('[YouTubeService] API fetch failed:', e.message);
        return null;
    }
}

// ============================================
// Cache (IndexedDB via Dexie)
// ============================================

async function loadFromCache() {
    try {
        const cached = await db.settings.get(CACHE_KEY);
        if (cached?.value) {
            const { videos, timestamp } = cached.value;
            if (videos && videos.length > 0) {
                lastFetchTimestamp = timestamp;
                console.log('[YouTubeService] Loaded', videos.length, 'videos from cache, age:', Math.round((Date.now() - timestamp) / 1000 / 60), 'min');
                return videos;
            }
        }
    } catch (e) {
        console.error('[YouTubeService] Cache load error:', e);
    }
    return null;
}

async function saveToCache(videos) {
    try {
        await db.settings.put({
            key: CACHE_KEY,
            value: { videos, timestamp: Date.now() }
        });
        console.log('[YouTubeService] Cached', videos.length, 'videos to IndexedDB');
    } catch (e) {
        console.error('[YouTubeService] Cache save error:', e);
    }
}

// ============================================
// Listeners
// ============================================

function notifyListeners() {
    listeners.forEach(cb => {
        try { cb(cachedVideos); } catch (e) { console.error('[YouTubeService] Listener error:', e); }
    });
}

export function subscribeToYouTubeCache(callback) {
    listeners.add(callback);
    return () => listeners.delete(callback);
}

// ============================================
// Initialization (follows cosyflowsCacheService pattern)
// ============================================

export async function initYouTubeCache() {
    if (initPromise) return initPromise;

    initPromise = (async () => {
        // 1. Load from IndexedDB cache first (instant)
        const cached = await loadFromCache();
        if (cached) {
            cachedVideos = cached;
            notifyListeners();
        }

        // 2. Check if cache is stale
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (!cached || cacheAge > CACHE_DURATION) {
            console.log('[YouTubeService] Cache stale, fetching from API in background...');
            refreshInBackground();
        }

        // 3. If no cache at all, use fallback immediately
        if (!cachedVideos) {
            cachedVideos = FALLBACK_VIDEOS;
            notifyListeners();
        }

        return cachedVideos;
    })();

    return initPromise;
}

async function refreshInBackground() {
    try {
        const fresh = await fetchFromAPI();
        if (fresh && fresh.length > 0) {
            cachedVideos = fresh;
            lastFetchTimestamp = Date.now();
            await saveToCache(fresh);
            notifyListeners();
            console.log('[YouTubeService] Background refresh complete:', fresh.length, 'videos');
        }
    } catch (e) {
        console.error('[YouTubeService] Background refresh failed:', e);
    }
}

// ============================================
// Public API
// ============================================

const VALID_VIDEO_ID = /^[a-zA-Z0-9_-]{11}$/;

function sanitizeVideoId(videoId) {
    if (typeof videoId === 'string' && VALID_VIDEO_ID.test(videoId)) return videoId;
    return '';
}

export function getThumbnailUrl(videoId, quality = 'hqdefault') {
    const id = sanitizeVideoId(videoId);
    if (!id) return '';
    return `https://i.ytimg.com/vi/${id}/${quality}.jpg`;
}

export function getWatchUrl(videoId) {
    const id = sanitizeVideoId(videoId);
    if (!id) return '';
    return `https://www.youtube.com/watch?v=${id}`;
}

export function getEmbedUrl(videoId) {
    const id = sanitizeVideoId(videoId);
    if (!id) return '';
    return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
}

export function getAllVideos() {
    return cachedVideos || FALLBACK_VIDEOS;
}

export function getVideosForPage(pageName) {
    const tags = PAGE_TAGS[pageName];
    if (!tags || tags.length === 0) return [];

    const videos = cachedVideos || FALLBACK_VIDEOS;
    return videos.filter(video =>
        video.tags && video.tags.some(tag => tags.includes(tag))
    );
}

export function getChannelUrl() {
    return 'https://www.youtube.com/@promptusai';
}

export async function forceRefresh() {
    console.log('[YouTubeService] Force refreshing...');
    const fresh = await fetchFromAPI();
    if (fresh && fresh.length > 0) {
        cachedVideos = fresh;
        lastFetchTimestamp = Date.now();
        await saveToCache(fresh);
        notifyListeners();
    }
    return cachedVideos;
}
